import { useState } from "react";
import { Button, Input, Modal } from "antd";
import { RefreshCw } from "lucide-react";
import Toast from "pages/data-table-view/components/Toast";
import {
  getGhlApiErrorMessage,
  syncGhlEmail,
  syncGhlFunnel,
  verifyGhlEmailOtp,
  verifyGhlFunnelOtp,
} from "services/ghl-integration.service";

const PRESETS = {
  funnel: {
    label: "Sync GHL Data",
    successMessage: "GHL data synced successfully",
    otpDescription:
      "Enter the 6-digit OTP sent to the configured GHL email to finish syncing funnel data.",
    sync: syncGhlFunnel,
    verifyOtp: verifyGhlFunnelOtp,
    expiredPattern: /login token not found|call sync\/funnel first|session/i,
  },
  email: {
    label: "Sync Email Data",
    successMessage: "GHL email data synced successfully",
    otpDescription:
      "Enter the 6-digit OTP sent to the configured GHL email to finish syncing email data.",
    sync: syncGhlEmail,
    verifyOtp: verifyGhlEmailOtp,
    expiredPattern: /login token not found|call .*\/email\/sync first|session/i,
  },
};

export default function GhlFunnelSyncButton({ api, onSynced, syncType = "funnel" }) {
  const preset = PRESETS[syncType] || PRESETS.funnel;
  const [isSyncing, setIsSyncing] = useState(false);
  const [otpOpen, setOtpOpen] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });

  const showToast = (message, type) => setToast({ show: true, message, type });

  const closeOtpModal = () => {
    if (isVerifying) return;
    setOtpOpen(false);
    setOtp("");
    setOtpError(null);
  };

  const handleSyncSuccess = async (message) => {
    setOtpOpen(false);
    setOtp("");
    setOtpError(null);
    await onSynced?.();
    showToast(message || preset.successMessage, "success");
  };

  const handleSync = async () => {
    setIsSyncing(true);
    try {
      const result = await preset.sync(api);
      if (result.requiresOtp) {
        setOtp("");
        setOtpError(null);
        setOtpOpen(true);
        showToast(result.message || "OTP has been sent to your registered email.", "info");
        return;
      }
      await handleSyncSuccess(result.message || preset.successMessage);
    } catch (error) {
      showToast(getGhlApiErrorMessage(error, "GHL sync failed. Please try again."), "error");
    } finally {
      setIsSyncing(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!/^\d{6}$/.test(otp)) {
      setOtpError("Enter a valid 6-digit OTP.");
      return;
    }

    setIsVerifying(true);
    setOtpError(null);
    try {
      const result = await preset.verifyOtp(api, { otp });
      await handleSyncSuccess(result.message || preset.successMessage);
    } catch (error) {
      const message = getGhlApiErrorMessage(error, "OTP verification failed. Please try again.");
      if (preset.expiredPattern.test(message)) {
        setOtpOpen(false);
        setOtp("");
        setOtpError(null);
        showToast(message, "error");
        return;
      }

      setOtpError(message);
    } finally {
      setIsVerifying(false);
    }
  };

  const busy = isSyncing || isVerifying;

  return (
    <>
      <Button
        type="default"
        className="inline-flex items-center gap-2"
        onClick={handleSync}
        disabled={busy}
        loading={isSyncing}
      >
        <RefreshCw className={`h-4 w-4 ${isSyncing ? "animate-spin" : ""}`} />
        {preset.label}
      </Button>

      <Modal
        title="Verify GHL OTP"
        open={otpOpen}
        onCancel={closeOtpModal}
        okText="Verify & Sync"
        onOk={handleVerifyOtp}
        confirmLoading={isVerifying}
        okButtonProps={{ disabled: otp.length !== 6 }}
        destroyOnClose
        maskClosable={!isVerifying}
        closable={!isVerifying}
      >
        <p className="text-sm text-gray-600 mb-3">{preset.otpDescription}</p>
        <Input
          value={otp}
          onChange={(e) => {
            const next = e.target.value.replace(/\D/g, "").slice(0, 6);
            setOtp(next);
            if (otpError) setOtpError(null);
          }}
          onPressEnter={handleVerifyOtp}
          maxLength={6}
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="000000"
          disabled={isVerifying}
          status={otpError ? "error" : undefined}
          size="large"
          className="tracking-[0.35em] text-center font-mono"
        />
        {otpError && <p className="text-sm text-red-600 mt-2">{otpError}</p>}
      </Modal>

      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ show: false, message: "", type: "" })}
        />
      )}
    </>
  );
}
